/**
 * 加载遮罩组件
 * 用于显示导出进度
 */

import React from 'react'
import { useTranslation } from 'react-i18next'

interface LoadingOverlayProps {
  isVisible: boolean
  progress?: number
  message?: string
  onCancel?: () => void
}

/**
 * 加载遮罩组件
 * 显示导出进度和取消按钮
 */
export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  isVisible,
  progress = 0,
  message,
  onCancel
}) => {
  const { t } = useTranslation()
  if (!isVisible) return null

  const displayMessage = message || t('export.modal.exporting')

  // 根据进度获取状态文本
  const getStatusText = (progress: number) => {
    if (progress < 30) return t('export.modal.preparing')
    if (progress >= 30 && progress < 70) return t('export.modal.processing')
    if (progress >= 70 && progress < 90) return t('export.modal.generating')
    return t('export.modal.completed')
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {displayMessage}
          </h3>
          {onCancel && (
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title={t('export.modal.cancelExport')}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        {/* 进度条 */}
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>{t('export.modal.progress')}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* 状态信息 */}
        <div className="text-center">
          <div className="inline-flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2" />
            <span className="text-sm text-gray-600">
              {getStatusText(progress)}
            </span>
          </div>
        </div>

        {/* 取消按钮 */}
        {onCancel && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              {t('export.modal.cancelExport')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}